import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { logError } from 'utils/logger';
import { Box, Button, Stack } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { useNotifications } from 'modules/notification';
import { DateRangeWithPeriods, InputField, SelectField, SwitchField } from 'components/form';

import { FilterSets, FilterSet } from './FilterSets';
import { ReportsListingFilters } from '../../types';
import { VOApiFilterType } from '../../../vendorOpportunity';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import { useFilterOptions } from '../../../vendorOpportunity/list/api';
import { useStagesReportDefaultFilters } from '../../hooks/useStagesReportDefaultFilters';

const toKey = (name: string) =>
  name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const parseSavedFilters = (
  value: ReportsListingFilters | undefined,
  fallback: ReportsListingFilters
): ReportsListingFilters => {
  if (!value) return fallback;

  return {
    ...value,
    from: value.from ? moment(value.from) : fallback.from,
    to: value.to ? moment(value.to) : fallback.to,
  };
};

interface Props {
  onSubscribe: (filters: ReportsListingFilters) => void;
}

export const ReportFilters = ({ onSubscribe }: Props) => {
  const { initialFilters } = useStagesReportDefaultFilters();

  const [lsFiltersValue, setLSFiltersValue] = useLocalStorage<Record<string, FilterSet<ReportsListingFilters>>>(
    'stagesFilter',
    {
      my: { label: 'My Pipeline', value: initialFilters },
    }
  );

  const [activeKey, setActiveKey] = useState<string>('my');
  const { showNotification } = useNotifications();

  const { options: salesOptions, loading: salesOptionsLoading } = useFilterOptions(VOApiFilterType.SALES_REP);
  const { options: salesManagerOptions, loading: salesManagerOptionsLoading } = useFilterOptions(
    VOApiFilterType.SALES_MANAGER
  );
  const { options: financeManagerOptions, loading: financeManagerOptionsLoading } = useFilterOptions(
    VOApiFilterType.FINANCE_MANAGER
  );
  const { options: locationOptions, loading: locationOptionsLoading } = useFilterOptions(VOApiFilterType.LOCATION);
  const {
    options: stageNameOptions,
    loading: stageOptionsLoading,
    refetch: refetchStageNameOptions,
  } = useFilterOptions(VOApiFilterType.BPF_STAGE_NAME);
  const { options: departmentOptions, loading: departmentOptionsLoading } = useFilterOptions(
    VOApiFilterType.BPF_DEPARTMENT
  );

  const { control, setValue, reset } = useForm<{ filters: ReportsListingFilters }>({
    defaultValues: { filters: parseSavedFilters(lsFiltersValue[activeKey]?.value, initialFilters) },
  });

  const filters = useWatch({ control, name: 'filters' });

  const isMounted = useRef<boolean>(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      const initialParsed = parseSavedFilters(lsFiltersValue[activeKey]?.value, initialFilters);
      onSubscribe(initialParsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncFilters = (next: ReportsListingFilters) => {
    setLSFiltersValue(prev => ({ ...prev, [activeKey]: { ...prev[activeKey], value: next } }));
    onSubscribe(next);
  };

  const handleSwitchFilter = (key: string) => {
    setActiveKey(key);
    const converted = parseSavedFilters(lsFiltersValue[key]?.value, initialFilters);
    reset({ filters: converted });
    onSubscribe(converted);
  };

  const handleDeleteFilter = (key: string) => {
    setLSFiltersValue(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    if (activeKey === key) handleSwitchFilter('my');
  };

  const handleAddFilter = (name: string, value: ReportsListingFilters) => {
    const key = toKey(name);

    if (!key) return;

    if (lsFiltersValue[key]) {
      showNotification(`Filter "${name}" already exists`, { type: 'warning' });
      return;
    }

    setLSFiltersValue(prev => ({ ...prev, [key]: { label: name, value } }));
    setActiveKey(key);
  };

  const handleSelectRange = async (range: { endDate: moment.Moment; startDate: moment.Moment }) => {
    setValue('filters.to', range.endDate);
    setValue('filters.from', range.startDate);
    syncFilters({ ...filters, to: range.endDate, from: range.startDate });
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto auto', gap: '1rem' }}>
      <FilterSets
        sets={lsFiltersValue}
        activeKey={activeKey}
        currentValue={filters}
        onAdd={handleAddFilter}
        onSwitch={handleSwitchFilter}
        onDelete={handleDeleteFilter}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <DateRangeWithPeriods
          control={control}
          nameTo="filters.to"
          nameFrom="filters.from"
          onRangeChange={handleSelectRange}
        />

        <Button
          onClick={() => {
            reset({ filters: initialFilters });
            syncFilters(initialFilters);
            refetchStageNameOptions({ input: { filterType: VOApiFilterType.BPF_STAGE_NAME } }).catch(logError);
          }}
        >
          Reset
        </Button>
      </Stack>

      <Stack gap="0.5rem" direction="row">
        <InputField
          label="Search"
          control={control}
          name="filters.search"
          inputProps={{ variant: 'outlined' }}
          extraAction={{
            debounceTime: 300,
            cb: search => syncFilters({ ...filters, search }),
          }}
        />

        <SelectField
          control={control}
          variant="outlined"
          label="Salesperson"
          options={salesOptions}
          name="filters.salesPersonId"
          isLoading={salesOptionsLoading}
          extraAction={nextValue => syncFilters({ ...filters, salesPersonId: nextValue.toString() })}
        />

        <SelectField
          control={control}
          variant="outlined"
          label="Sales Manager"
          options={salesManagerOptions}
          name="filters.salesManagerId"
          isLoading={salesManagerOptionsLoading}
          extraAction={nextValue => syncFilters({ ...filters, salesManagerId: nextValue.toString() })}
        />

        <SelectField
          control={control}
          variant="outlined"
          label="Finance Manager"
          options={financeManagerOptions}
          name="filters.financeManagerId"
          isLoading={financeManagerOptionsLoading}
          extraAction={nextValue => syncFilters({ ...filters, financeManagerId: nextValue.toString() })}
        />

        <SelectField
          label="Location"
          control={control}
          variant="outlined"
          name="filters.locationId"
          options={locationOptions}
          isLoading={locationOptionsLoading}
          extraAction={nextValue => syncFilters({ ...filters, locationId: nextValue.toString() })}
        />

        <SelectField
          label="Department"
          control={control}
          variant="outlined"
          options={departmentOptions}
          name="filters.departmentId"
          isLoading={departmentOptionsLoading}
          extraAction={nextValue => {
            setValue('filters.stageName', '');
            syncFilters({ ...filters, departmentId: nextValue.toString(), stageName: '' });
            refetchStageNameOptions({
              input: {
                filterType: VOApiFilterType.BPF_STAGE_NAME,
                metaVariables: { departmentId: nextValue.toString() },
              },
            }).catch(logError);
          }}
        />

        <SelectField
          label="Stage"
          control={control}
          variant="outlined"
          name="filters.stageName"
          options={stageNameOptions}
          isLoading={stageOptionsLoading}
          extraAction={nextValue => syncFilters({ ...filters, stageName: nextValue.toString() })}
        />

        <SwitchField
          control={control}
          name="filters.isIncludeClosedDeals"
          label="Include closed deals"
          extraAction={nextValue => syncFilters({ ...filters, isIncludeClosedDeals: nextValue as boolean })}
        />
      </Stack>
    </Box>
  );
};
